<template>
  <div v-if="isButtonVisible">
    <!-- Duplicate Order Button -->
    <b-button
      variant="outline-info"
      class="font-weight-bolder"
      :disabled="isDuplicating || !hasValidProducts"
      @click="showDuplicateConfirmModal"
    >
      <b-spinner
        v-if="isDuplicating"
        small
        class="mr-50"
      />
      {{ isDuplicating ? 'Duplicating...' : 'Duplicate Order' }}
    </b-button>

    <!-- Same Collection Modal (Retailer only) -->
    <b-modal
      id="duplicate-order-same-collection"
      ref="sameCollectionModal"
      centered
      hide-footer
      hide-header
      hide-header-close
    >
      <div
        class="
          pb-5-px
          border-bottom
          d-flex
          justify-content-between
          align-items-center
        "
      >
        <span class="custom-modal-heading color-primary">
          Add to Cart
        </span>
        <feather-icon
          class="float-right color-neutral-black cursor-pointer"
          icon="XIcon"
          size="24"
          @click="closeSameCollectionModal"
        />
      </div>
      <div class="mt-1">
        <b-card-text class="mb-1">
          Your cart already contains products from this collection.
        </b-card-text>
        <b-card-text>
          Would you like to keep existing products or clear the cart?
        </b-card-text>
        <div class="elements-center my-1">
          <b-button
            variant="outline-secondary-black mr-1"
            :disabled="isDuplicating"
            @click="duplicateWithMerge"
          >
            Keep existing products
          </b-button>
          <b-button
            variant="info"
            :disabled="isDuplicating"
            @click="duplicateWithClear"
          >
            Clear cart
          </b-button>
        </div>
      </div>
    </b-modal>

    <!-- Different Collection Modal (Retailer only) -->
    <b-modal
      id="duplicate-order-different-collection"
      ref="differentCollectionModal"
      centered
      hide-footer
      hide-header
      hide-header-close
    >
      <div
        class="
          pb-5-px
          border-bottom
          d-flex
          justify-content-between
          align-items-center
        "
      >
        <span class="custom-modal-heading color-primary">
          Different Collection
        </span>
        <feather-icon
          class="float-right color-neutral-black cursor-pointer"
          icon="XIcon"
          size="24"
          @click="closeDifferentCollectionModal"
        />
      </div>
      <div class="mt-1">
        <b-card-text class="mb-1">
          Your cart contains products from a different collection ({{ cartCollectionName }}).
        </b-card-text>
        <b-card-text>
          To proceed, your existing cart will need to be cleared.
        </b-card-text>
        <div class="elements-center my-1">
          <b-button
            variant="outline-secondary-black mr-1"
            @click="closeDifferentCollectionModal"
          >
            Cancel
          </b-button>
          <b-button
            variant="danger"
            :disabled="isDuplicating"
            @click="duplicateWithClear"
          >
            Clear cart and proceed
          </b-button>
        </div>
      </div>
    </b-modal>

    <!-- Duplicate Order Confirmation Modal -->
    <b-modal
      id="duplicate-order-confirmation"
      ref="duplicateConfirmModal"
      centered
      hide-footer
      hide-header
      hide-header-close
    >
      <div
        class="
          pb-5-px
          border-bottom
          d-flex
          justify-content-between
          align-items-center
        "
      >
        <span class="custom-modal-heading color-primary">
          Duplicate Order
        </span>
        <feather-icon
          class="float-right color-neutral-black cursor-pointer"
          icon="XIcon"
          size="24"
          @click="closeDuplicateConfirmModal"
        />
      </div>
      <div class="mt-1">
        <b-card-text class="mb-1">
          This will remove all cancelled and removed styles from the duplicated order.
        </b-card-text>
        <b-card-text>
          Do you want to continue?
        </b-card-text>
        <div class="elements-center my-1">
          <b-button
            variant="outline-secondary-black mr-1"
            @click="closeDuplicateConfirmModal"
          >
            Cancel
          </b-button>
          <b-button
            variant="info"
            :disabled="isDuplicating"
            @click="confirmDuplicateOrder"
          >
            Continue
          </b-button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
 BButton, BModal, BCardText, BSpinner,
} from 'bootstrap-vue'
import FeatherIcon from '@/@core/components/feather-icon/FeatherIcon.vue'
import { apiToastSuccess, apiToastWarning } from '@/@core/utils/toast'
import {
  ADD_PRODUCT_TO_CART,
  FETCH_CART_PRODUCTS,
  FETCH_CART_COUNT,
  SET_DUPLICATE_ORDER_DATA,
  UPDATE_SELECTED_DIRECT_RETAILER,
  GET_PRODUCTS_BY_IDS,
} from '@/store/modules/shop.module'
import { FETCH_DIRECT_RETAILERS } from '@/store/modules/connections.module'
import constants from '@/constants'
import analytics from '@/@core/utils/analytics'
import { formatObject } from '@/@core/utils/utils'
import { mapState } from 'vuex'
import UserRoleMixinVue from '../UserRoleMixin.vue'

const { ORDER_STATUS, ORDER_PRODUCT_SHIPMENT_STATUS, TRACKS } = constants

export default {
  name: 'DuplicateOrderButton',
  components: {
    BButton,
    BModal,
    BCardText,
    BSpinner,
    FeatherIcon,
  },
  mixins: [UserRoleMixinVue],
  props: {
    order: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      isDuplicating: false,
      cartCollectionId: null,
      cartCollectionName: null,
      retailerValidatedProducts: [], // Products validated for retailer visibility
    }
  },
  computed: {
    ...mapState({
      directRetailers: state => state.connections.directRetailers || [],
    }),
    isButtonVisible() {
      // Hide for fully cancelled orders
      return this.order.status !== ORDER_STATUS.CANCELLED
    },
    validProducts() {
      return (this.order.products || []).filter(
        product => product.status !== ORDER_PRODUCT_SHIPMENT_STATUS.CANCELLED.LABEL,
      )
    },
    hasValidProducts() {
      return this.validProducts.length > 0
    },
    hasExcludedProducts() {
      // Check if there are any cancelled/removed products that will be excluded
      const totalProducts = this.order.products?.length || 0
      const validCount = this.validProducts.length
      return totalProducts > validCount
    },
  },
  methods: {
    showDuplicateConfirmModal() {
      if (!this.hasValidProducts) {
        apiToastWarning('No valid products to duplicate')
        return
      }
      // Only show modal if there are excluded products
      if (this.hasExcludedProducts) {
        this.$bvModal.show('duplicate-order-confirmation')
      } else {
        this.handleDuplicateOrder()
      }
    },
    closeDuplicateConfirmModal() {
      this.$bvModal.hide('duplicate-order-confirmation')
    },
    confirmDuplicateOrder() {
      this.closeDuplicateConfirmModal()
      this.handleDuplicateOrder()
    },
    async handleDuplicateOrder() {
      this.trackDuplicateOrderClick()

      if (this.isBrand) {
        // Brand flow: Navigate to collection with pre-filled cart popup
        this.handleBrandDuplicateOrder()
      } else {
        // Retailer flow: Add to cart
        this.handleRetailerDuplicateOrder()
      }
    },

    // Brand flow - navigate to collection and open cart popup
    async handleBrandDuplicateOrder() {
      this.isDuplicating = true

      try {
        // Fetch directRetailers if not already loaded (needed to get retailer email)
        let retailers = this.directRetailers
        if (!retailers.length) {
          retailers = await this.$store.dispatch(FETCH_DIRECT_RETAILERS)
        }

        // Find retailer from directRetailers to get the email (order object doesn't have retailerEmail)
        const matchedRetailer = retailers.find(
          r => r.retailerId === this.order.retailerId,
        )

        // Prepare retailer data for pre-selection
        const retailerData = {
          retailerId: this.order.retailerId,
          entityName: this.order.retailerName,
          email: matchedRetailer?.email || '',
          discount: this.order.discount ?? 1,
        }

        // Store duplicate order data in Vuex
        this.$store.commit(SET_DUPLICATE_ORDER_DATA, {
          products: this.validProducts,
          retailer: retailerData,
          orderId: this.order.orderSeqId,
          collectionId: this.order.collectionId,
        })

        // Pre-select the retailer
        this.$store.commit(UPDATE_SELECTED_DIRECT_RETAILER, retailerData)

        // Note: trackDuplicateOrderSuccess() is called in ShopProducts.vue after products are verified
        // This ensures accurate analytics - success is only tracked when products can actually be loaded

        // Navigate to the collection products page
        this.$router.push({
          name: 'collection/products',
          params: { collectionId: this.order.collectionId },
          query: { duplicateOrder: 'true' },
        })
      } catch (error) {
        // error toast handled by axios interceptor
      } finally {
        this.isDuplicating = false
      }
    },

    // Validate products visibility for retailer (filters out segmented products)
    async validateProductsVisibility() {
      const productIds = this.validProducts.map(p => p.productId || p._id)

      if (!productIds.length) {
        return false
      }

      try {
        const res = await this.$store.dispatch(GET_PRODUCTS_BY_IDS, {
          payload: { productIds },
        })

        this.filterVisibleProducts(res?.data?.data || [])

        return this.retailerValidatedProducts.length > 0
      } catch (error) {
        apiToastWarning('Failed to validate products')
        return false
      }
    },

    filterVisibleProducts(visibleProducts) {
      const visibleProductIds = visibleProducts.map(p => p._id)

      this.retailerValidatedProducts = this.validProducts.filter(
        p => visibleProductIds.includes(p.productId || p._id),
      )

      if (this.retailerValidatedProducts.length < this.validProducts.length) {
        apiToastWarning('Some products were removed as they are no longer available')
      }
    },

    // Retailer flow - add to cart
    async handleRetailerDuplicateOrder() {
      this.isDuplicating = true

      try {
        const hasValidProducts = await this.validateProductsVisibility()

        if (!hasValidProducts) {
          apiToastWarning('No products available to duplicate')
          this.isDuplicating = false
          return
        }

        await this.processRetailerCart()
      } catch (error) {
        this.isDuplicating = false
      }
    },

    async processRetailerCart() {
      const cartResponse = await this.$store.dispatch(FETCH_CART_PRODUCTS)
      const cartData = cartResponse?.data?.data
      const cart = Array.isArray(cartData) ? cartData[0] : cartData

      const isCartEmpty = !cart?.products?.length

      if (isCartEmpty) {
        await this.addProductsToCart(false)
        return
      }

      this.handleCartWithProducts(cart)
    },

    handleCartWithProducts(cart) {
      this.cartCollectionId = cart.collectionId
      this.cartCollectionName = cart.collectionName || 'another collection'
      this.isDuplicating = false

      const isSameCollection = cart.collectionId === this.order.collectionId
      const modalRef = isSameCollection ? 'sameCollectionModal' : 'differentCollectionModal'
      this.$refs[modalRef].show()
    },

    async addProductsToCart(shouldClearCart = false) {
      this.isDuplicating = true
      this.closeAllModals()

      try {
        const products = this.transformOrderProductsForCart()
        const params = shouldClearCart ? { shouldClearCart: true } : {}

        await this.$store.dispatch(ADD_PRODUCT_TO_CART, {
          payload: { products },
          params,
        })

        await this.$store.dispatch(FETCH_CART_COUNT)

        apiToastSuccess('Products added to cart!')
        this.trackDuplicateOrderSuccess()
        this.$router.push({ name: 'checkout' })
      } catch (error) {
        this.handleAddToCartError(error)
      } finally {
        this.isDuplicating = false
      }
    },

    handleAddToCartError(error) {
      if (error.response?.status === 425) {
        this.cartCollectionName = error.response?.data?.data?.collectionName || 'another collection'
        this.$refs.differentCollectionModal.show()
      }
    },

    async duplicateWithMerge() {
      // Add products without clearing cart (merge)
      await this.addProductsToCart(false)
    },

    async duplicateWithClear() {
      // Clear cart then add products
      await this.addProductsToCart(true)
    },

    transformOrderProductsForCart() {
      // Use retailerValidatedProducts for retailer flow (segmentation filtered)
      const productsToTransform = this.retailerValidatedProducts.length > 0
        ? this.retailerValidatedProducts
        : this.validProducts

      return productsToTransform
        .map(product => {
          if (product.isCarton) {
            return {
              productId: product.productId,
              quantity: product.quantity,
            }
          }
          return {
            productId: product.productId,
            items: (product.items || [])
              .filter(item => item.value > 0)
              .map(item => ({
                sku: item.sku || item.skuId,
                value: item.value,
              })),
          }
        })
        .filter(product => {
          if (product.quantity !== undefined) {
            return product.quantity > 0
          }
          return product.items && product.items.length > 0
        })
    },

    closeSameCollectionModal() {
      this.$refs.sameCollectionModal.hide()
      this.isDuplicating = false
    },

    closeDifferentCollectionModal() {
      this.$refs.differentCollectionModal.hide()
      this.isDuplicating = false
    },

    closeAllModals() {
      this.$refs.sameCollectionModal?.hide()
      this.$refs.differentCollectionModal?.hide()
    },

    // Analytics tracking methods
    trackDuplicateOrderClick() {
      const trackName = this.isBrand
        ? TRACKS.ACTIONS.SINGLE_ORDER.BRAND_CLICKS_DUPLICATE_ORDER
        : TRACKS.ACTIONS.SINGLE_ORDER.RETAILER_CLICKS_DUPLICATE_ORDER
      analytics.track(
        trackName,
        formatObject({
          orderId: this.order.orderSeqId,
          collectionId: this.order.collectionId,
          collectionName: this.order.collectionName,
          productCount: this.validProducts.length,
        }),
      )
    },

    trackDuplicateOrderSuccess() {
      const trackName = this.isBrand
        ? TRACKS.ACTIONS.SINGLE_ORDER.BRAND_DUPLICATE_ORDER_SUCCESS
        : TRACKS.ACTIONS.SINGLE_ORDER.RETAILER_DUPLICATE_ORDER_SUCCESS
      // For retailer flow, use retailerValidatedProducts count if available
      const productCount = !this.isBrand && this.retailerValidatedProducts.length > 0
        ? this.retailerValidatedProducts.length
        : this.validProducts.length
      analytics.track(
        trackName,
        formatObject({
          orderId: this.order.orderSeqId,
          collectionId: this.order.collectionId,
          productCount,
        }),
      )
    },
  },
}
</script>
